import React, { useState } from 'react';
import {
  Alert,
  Button,
  ButtonSize,
  Dropdown,
  type DropdownOption,
  type DropdownProps,
  Loader,
} from '@unmind/design-system';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';

import {
  useSaveUserLocation,
  useUserLocation,
  type UserLocationInput,
} from 'Account/hooks';
import { CANADA, countryRequiresState } from 'Account/hooks/useUserLocation';
import { usePractitionerCountries } from 'Book/shared/hooks';

export type IUserLocationInput = UserLocationInput;

/**
 * `Dropdown`'s `onChange` is typed for its multi-select mode too, so even a
 * single selection arrives as a union.
 */
type DropdownValue = Parameters<DropdownProps<string>['onChange']>[0];

/** Clearing the control gives `null`, which these fields store as `''`. */
const toSingleValue = (selected: DropdownValue) =>
  typeof selected === 'string' ? selected : '';

interface LocationDropdownProps {
  id: string;
  label: string;
  options: DropdownOption<string>[];
  value: string;
  onChange(value: string): void;
}

const LocationDropdown = ({
  id,
  label,
  options,
  value,
  onChange,
}: LocationDropdownProps) => (
  <div>
    <label className="hidden" htmlFor={id}>
      {label}
    </label>
    <Dropdown
      id={id}
      options={options}
      placeholder={label}
      selectedValue={value}
      onChange={selected => onChange(toSingleValue(selected))}
      floatMenu={true}
      closeOnScroll={false}
    />
  </div>
);

interface Props {
  onSubmitSuccess?(values: IUserLocationInput): void;
  OverrideSubmitButton?: React.ReactFragment;
}

export default ({ onSubmitSuccess, OverrideSubmitButton }: Props) => {
  const { t } = useTranslation('book', { keyPrefix: 'locationModal' });
  const [saveFailed, setSaveFailed] = useState(false);

  const { userLocationData, loading: userLocationDataLoading } =
    useUserLocation();

  const { saveLocation, saving } = useSaveUserLocation(
    userLocationData?.user?.id,
  );

  const {
    countryData,
    loading: practitionerCountriesDataLoading,
    getStateData,
  } = usePractitionerCountries();

  /**
   * A saved value the control cannot offer shows as a placeholder while the
   * form still holds it, leaving the form submittable with a value the user
   * never chose. Country and state are each kept only if they are on offer.
   */
  const getInitialValues = () => {
    const country = userLocationData?.user?.country?.value ?? '';
    const state = userLocationData?.user?.state?.value ?? '';

    if (!countryData.some(option => option.value === country)) {
      return { country: '', state: '' };
    }

    const isStateOnOffer =
      countryRequiresState(country) &&
      getStateData(country).some(option => option.value === state);

    return { country, state: isStateOnOffer ? state : '' };
  };

  if (userLocationDataLoading || practitionerCountriesDataLoading) {
    return <Loader />;
  }

  return (
    <Formik
      initialValues={getInitialValues()}
      onSubmit={async (values, actions) => {
        setSaveFailed(false);

        let saved;

        /**
         * Save location country and state
         */
        try {
          saved = await saveLocation({
            country: values.country,
            state: values.state || null,
          });
        } catch {
          setSaveFailed(true);

          return;
        } finally {
          actions.setSubmitting(false);
        }

        /**
         * Perform any callbacks provided on submit success
         */
        onSubmitSuccess?.(saved);
      }}
    >
      {({ values, handleSubmit, setFieldValue }) => {
        const stateFieldKey = values.country === CANADA ? 'province' : 'state';
        const isDisabled =
          values.country === '' ||
          (countryRequiresState(values.country) && !values.state);

        return (
          <form onSubmit={handleSubmit}>
            <fieldset>
              <LocationDropdown
                id="country"
                label={t('fields.country')}
                options={countryData}
                value={values.country}
                onChange={country => {
                  setFieldValue('country', country);
                  setFieldValue('state', '');
                }}
              />
              {countryRequiresState(values.country) && (
                <LocationDropdown
                  id="state"
                  label={t(`fields.${stateFieldKey}`)}
                  options={getStateData(values.country)}
                  value={values.state}
                  onChange={state => setFieldValue('state', state)}
                />
              )}
              {saveFailed && (
                <Alert variant="error" size="small" role="alert">
                  {t('error')}
                </Alert>
              )}
              {!!OverrideSubmitButton ? (
                OverrideSubmitButton
              ) : (
                <Button
                  label={t('cta')}
                  type="submit"
                  variant="primary"
                  size={ButtonSize.medium}
                  styling="full-width"
                  disabled={isDisabled || saving}
                  loading={saving}
                />
              )}
            </fieldset>
          </form>
        );
      }}
    </Formik>
  );
};
